import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import type { Notification } from "@/types/notification";

import {
  CATEGORY_LABEL,
  CATEGORY_VARIANT,
  relativeTime,
} from "./notification-utils";

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  /** Compact rendering for the header dropdown. */
  compact?: boolean;
}

/** Single notification row, shared by the header dropdown and the full page. */
export function NotificationItem({
  notification,
  onClick,
  compact = false,
}: NotificationItemProps) {
  const { title, body, category, created_at, is_read } = notification;
  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={cn(
        "flex w-full gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-accent/50",
        !is_read && "bg-primary/5"
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <span
          className={cn(
            "block line-clamp-2 text-sm",
            is_read ? "font-normal" : "font-medium"
          )}
        >
          {title}
        </span>
        {body && (
          <p
            className={cn(
              "text-xs text-muted-foreground",
              compact ? "line-clamp-2" : "line-clamp-3"
            )}
          >
            {body}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <Badge
            variant={CATEGORY_VARIANT[category]}
            className="px-1.5 py-0.5 text-[11px]"
          >
            {CATEGORY_LABEL[category]}
          </Badge>
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime(created_at)}
          </span>
        </div>
      </div>
    </button>
  );
}

export default NotificationItem;
